import React, { useEffect, useRef, useId, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toggleFavoriteProduct } from "../utils/wishlistApi";
import { getProductLink } from "../utils/productLink";

const ProductsSlider = ({ title, products, showTitle = true }) => {
  const sliderRef = useRef(null);
  const uniqueId = useId().replace(/:/g, "-");
  const sliderClass = `tf-sw-products-slider-${uniqueId}`;
  const { addItem, open, items: cartItems } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [localFavoriteState, setLocalFavoriteState] = useState({});

  const productsWithFavorite = useMemo(() => {
    return products.map((p) => ({
      ...p,
      is_favorite:
        localFavoriteState[p.id] !== undefined
          ? localFavoriteState[p.id]
          : p.is_favorite || false,
    }));
  }, [products, localFavoriteState]);

  useEffect(() => {
    // Wait for DOM to be ready and Swiper to be available
    const initializeSlider = () => {
      if (!window.Swiper) {
        console.log("Swiper not available yet, retrying...");
        setTimeout(initializeSlider, 100);
        return;
      }

      // Initialize ProductsSlider using ref
      if (sliderRef.current) {
        // Destroy existing instance if it exists
        if (sliderRef.current.swiper) {
          try {
            sliderRef.current.swiper.destroy(true, true);
          } catch (e) {
            // no-op
          }
        }

        console.log("Initializing ProductsSlider...");
        const sliderElement = sliderRef.current;
        const preview = parseFloat(sliderElement.dataset.preview) || 4;
        const tablet = parseFloat(sliderElement.dataset.tablet) || 3;
        const mobile = parseFloat(sliderElement.dataset.mobile) || 2.5;
        const spacingLg = parseInt(sliderElement.dataset.spaceLg) || 30;
        const spacingMd = parseInt(sliderElement.dataset.spaceMd) || 12;
        const perGroup = parseInt(sliderElement.dataset.pagination) || 1;
        const perGroupMd = parseInt(sliderElement.dataset.paginationMd) || 1;
        const perGroupLg = parseInt(sliderElement.dataset.paginationLg) || 1;

        new window.Swiper(sliderRef.current, {
          slidesPerView: mobile,
          spaceBetween: spacingMd,
          speed: 1000,
          slidesPerGroup: perGroup,
          breakpoints: {
            768: {
              slidesPerView: preview,
              spaceBetween: spacingLg,
              slidesPerGroup: perGroupLg,
            },
            1150: {
              slidesPerView: preview,
              spaceBetween: spacingLg,
              slidesPerGroup: perGroupLg,
            },
          },
          watchOverflow: true,
          observer: true,
          observeParents: true,
        });
        console.log("ProductsSlider initialized successfully");
      }
    };

    initializeSlider();
  }, [uniqueId, products]);

  return (
    <>
      <style>
        {`
          /* Maximum width constraint for products slider section */
          .special-offers-products-section.flat-spacing-2 {
            max-width: 1600px !important;
            margin: 0 auto !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            box-sizing: border-box !important;
          }

          @media (max-width: 575px) {
            .special-offers-products-section.flat-spacing-2 {
              max-width: 100% !important;
              padding-left: 12px !important;
              padding-right: 12px !important;
            }
          }

          @media (min-width: 576px) and (max-width: 767px) {
            .special-offers-products-section.flat-spacing-2 {
              max-width: 100% !important;
              padding-left: 16px !important;
              padding-right: 16px !important;
            }
          }

          @media (min-width: 768px) and (max-width: 991px) {
            .special-offers-products-section.flat-spacing-2 {
              max-width: 1200px !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          @media (min-width: 992px) and (max-width: 1199px) {
            .special-offers-products-section.flat-spacing-2 {
              max-width: 1400px !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          @media (min-width: 1400px) {
            .special-offers-products-section.flat-spacing-2 {
              max-width: 1600px !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          .${sliderClass} .swiper-slide {
            height: auto;
          }
          
          .${sliderClass} .card-product {
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          
          .${sliderClass} .card-product-wrapper {
            flex: 1;
            display: flex;
            flex-direction: column;
            position: relative;
          }
          
          /* Make discount chip stick to top left corner */
          .${sliderClass} .on-sale-wrap {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            z-index: 10 !important;
          }
          
          /* Hide navigation arrows */
          .${sliderClass} ~ .box-sw-navigation,
          .box-sw-navigation .nav-next-products-${uniqueId},
          .box-sw-navigation .nav-prev-products-${uniqueId} {
            display: none !important;
          }

          /* Hide pagination dots */
          .sw-pagination-products-${uniqueId} {
            display: none !important;
          }

          /* Cart icon bluish filled when product is in cart */
          .${sliderClass} .card-product .list-product-btn .box-icon.quick-add.product-in-cart {
            background-color: #0e5aa7 !important;
            color: #ffffff !important;
          }

          .${sliderClass} .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover {
            background-color: #0e5aa7 !important; /* Keep bluish background on hover */
            color: #ffffff !important;
          }

          .${sliderClass} .card-product .list-product-btn .box-icon.quick-add.product-in-cart .icon,
          .${sliderClass} .card-product .list-product-btn .box-icon.quick-add.product-in-cart .icon:before,
          .${sliderClass} .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover .icon,
          .${sliderClass} .card-product .list-product-btn .box-icon.quick-add.product-in-cart:hover .icon:before {
            color: #ffffff !important;
          }

          /* Make products smaller on mobile for 2.5 items */
          @media (max-width: 767px) {
            .${sliderClass} .swiper-slide {
              width: calc((100% - 24px) / 2.5) !important;
              flex-shrink: 0 !important;
              margin-right: 12px !important;
            }
            
            .${sliderClass} .card-product {
              width: 100%;
              min-width: 0;
            }
          }
        `}
      </style>
      <section
        className="flat-spacing-2 special-offers-products-section"
        style={{ marginTop: "10px", paddingTop: "5px" }}
      >
        <div className="container">
          {showTitle && title && (
            <div
              className="flat-title flex-row justify-content-center px-0"
              style={{ marginBottom: "30px" }}
            >
              <span
                className="title wow fadeInUp"
                data-wow-delay="0s"
                style={{
                  fontSize: "1.6rem",
                  fontWeight: "600",
                  color: "#0e5aa7",
                  textAlign: "center",
                }}
              >
                {title}
              </span>
            </div>
          )}
        </div>
        <div
          className="container-full slider-layout-right"
          style={{ position: "relative" }}
        >
          <div
            ref={sliderRef}
            dir="ltr"
            className={`swiper ${sliderClass} sw-wrapper-right`}
            data-preview="4"
            data-tablet="3"
            data-mobile="2.5"
            data-space-lg="30"
            data-space-md="12"
            data-pagination="1"
            data-pagination-md="1"
            data-pagination-lg="1"
          >
            <div className="swiper-wrapper">
              {productsWithFavorite.map((p) => (
                <div key={p.id} className="swiper-slide">
                  <div
                    className="card-product grid"
                    data-availability={p.availability}
                    data-brand={p.brand}
                  >
                    <div className="card-product-wrapper">
                      <Link
                        to={getProductLink(p)}
                        state={{ product: p }}
                        className="product-img"
                      >
                        <img
                          className="lazyload img-product"
                          data-src={
                            p.image ||
                            `https://erp-elsalab.odoo.com/web/image/${p.id}`
                          }
                          src={
                            p.image ||
                            `https://erp-elsalab.odoo.com/web/image/${p.id}`
                          }
                          alt={p.title || p.name}
                        />
                        <img
                          className="lazyload img-hover"
                          data-src={
                            p.image ||
                            `https://erp-elsalab.odoo.com/web/image/${p.id}`
                          }
                          src={
                            p.image ||
                            `https://erp-elsalab.odoo.com/web/image/${p.id}`
                          }
                          alt={p.title || p.name}
                        />
                      </Link>
                      {p.list_price != null &&
                        p.price != null &&
                        Number(p.list_price) > 0 &&
                        Number(p.list_price) > Number(p.price) &&
                        Math.round(
                          ((Number(p.list_price) - Number(p.price)) /
                            Number(p.list_price)) *
                            100
                        ) > 0 && (
                          <div
                            className="on-sale-wrap"
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              zIndex: 10,
                            }}
                          >
                            <div
                              className="on-sale-item"
                              style={{
                                backgroundColor: "var(--primary)",
                                color: "#fff",
                              }}
                            >
                              {Math.round(
                                ((Number(p.list_price) - Number(p.price)) /
                                  Number(p.list_price)) *
                                  100
                              )}
                              % OFF
                            </div>
                          </div>
                        )}
                      <div className="list-product-btn absolute-2">
                        <a
                          href="#"
                          className={`box-icon bg_white quick-add tf-btn-loading ${
                            cartItems.some((item) => item.id === String(p.id))
                              ? "product-in-cart"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            addItem({ ...p, qty: 1 });
                            open();
                          }}
                        >
                          <span className="icon icon-bag"></span>
                          <span className="tooltip">Add to cart</span>
                        </a>
                        <button
                          type="button"
                          className={`box-icon bg_white wishlist btn-icon-action ${
                            p.is_favorite ? "active" : ""
                          }`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const currentFavorite = p.is_favorite || false;
                              const newFavoriteState = !currentFavorite;

                              // Optimistically update local state
                              setLocalFavoriteState((prev) => ({
                                ...prev,
                                [p.id]: newFavoriteState,
                              }));

                              await toggleFavoriteProduct(
                                parseInt(p.id),
                                newFavoriteState,
                                null,
                                p
                              );

                              console.log(
                                `Product ${p.id} ${
                                  newFavoriteState ? "added to" : "removed from"
                                } wishlist`
                              );
                            } catch (error) {
                              // Revert on error
                              setLocalFavoriteState((prev) => ({
                                ...prev,
                                [p.id]: p.is_favorite || false,
                              }));
                              console.error(
                                "Failed to toggle favorite:",
                                error
                              );
                              alert(
                                error.message || "Failed to update wishlist"
                              );
                            }
                          }}
                        >
                          <span className="icon icon-heart"></span>
                          <span className="tooltip">
                            {p.is_favorite
                              ? "Remove from Wishlist"
                              : "Add to Wishlist"}
                          </span>
                          <span className="icon icon-delete"></span>
                        </button>
                      </div>
                    </div>
                    <div className="card-product-info">
                      <Link
                        to={getProductLink(p)}
                        state={{ product: p }}
                        className="title link"
                      >
                        {p.title || p.name}
                      </Link>
                      {p.price != null && (
                        <div className="price-area">
                          <span
                            className="price current-price"
                            style={{ color: "var(--primary)" }}
                          >
                            EGP {Number(p.price).toFixed(1)}
                          </span>
                          {p.list_price != null &&
                            Number(p.list_price) > Number(p.price) && (
                              <>
                                <span
                                  className="price compare-at-price"
                                  style={{
                                    color: "#000",
                                    marginLeft: 8,
                                  }}
                                >
                                  {Math.round(Number(p.list_price))}
                                </span>
                              </>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsSlider;
